import React from "react";
import { Card, CardBody, CardHeader, Table } from "reactstrap";
import { useOosValidation, OosWindow, GridRealityCheck } from "../hooks/useOosValidation";
import { WINDOW_LABELS } from "../utils/windows";

function pct(v: number | null, digits = 1): string {
  return v !== null ? `${v.toFixed(digits)}%` : "—";
}

function signedPct(v: number | null, digits = 1): string {
  if (v === null) return "—";
  return `${v >= 0 ? "+" : ""}${v.toFixed(digits)}%`;
}

function ci(pair: [number, number] | null, digits = 1): string {
  if (!pair) return "";
  return `${pair[0].toFixed(digits)}–${pair[1].toFixed(digits)}`;
}

/** Green when the edge over the matched base rate is positive, muted/red otherwise. */
function edgeColor(v: number | null): string {
  if (v === null) return "var(--text-muted)";
  if (v > 0) return "var(--success)";
  return "var(--error)";
}

/**
 * RealityCheckPanel — the headline anti-data-snooping result. `reality_check_p`
 * is the date-clustered recentered-bootstrap probability that the single
 * best-looking rule across the whole (window × horizon × conviction) grid beats
 * its matched base rate by chance alone. Small (< 0.05) = the edge survives the
 * search and is not a data-mining artifact.
 */
function RealityCheckPanel({ rc }: { rc: GridRealityCheck }) {
  const p = rc.reality_check_p;
  const survives = p !== null && p < 0.05;
  const bin = rc.best_rule?.conviction_bin;
  const winLabel = rc.best_rule ? (WINDOW_LABELS[String(rc.best_rule.window)] ?? `w${rc.best_rule.window}`) : null;

  return (
    <Card className="mb-4" style={{ borderLeft: `4px solid ${survives ? "var(--success)" : "var(--border-color)"}` }}>
      <CardBody>
        <div className="d-flex align-items-baseline justify-content-between flex-wrap gap-2 mb-2">
          <h5 className="mb-0" style={{ fontWeight: 700 }}>Does the edge survive the search?</h5>
          <span
            className={`badge ${survives ? "bg-success" : "bg-secondary"}`}
            style={{ fontSize: 12 }}
          >
            {survives ? "✓ Survives" : "Not yet significant"}
          </span>
        </div>
        <p className="text-muted mb-3" style={{ fontSize: 13.5 }}>
          We evaluate <strong>every</strong> rule in the {rc.n_rules.toLocaleString()}-rule selection grid
          (window × horizon × conviction bin) and ask, via White's Reality Check, whether the single
          best-looking one could have beaten its matched base rate by chance. A small p-value means the
          edge is real, not cherry-picked.
        </p>
        <div className="d-flex flex-wrap gap-4">
          <div>
            <div style={{ fontSize: 26, fontWeight: 700, color: survives ? "var(--success)" : "var(--text)", fontFamily: "var(--font-mono)" }}>
              {p !== null ? p.toFixed(3) : "—"}
            </div>
            <div className="text-muted" style={{ fontSize: 12 }}>Reality Check p-value</div>
          </div>
          <div>
            <div style={{ fontSize: 26, fontWeight: 700, color: "var(--text)", fontFamily: "var(--font-mono)" }}>
              {rc.bonferroni_p !== null ? rc.bonferroni_p.toFixed(3) : "—"}
            </div>
            <div className="text-muted" style={{ fontSize: 12 }}>Bonferroni floor</div>
          </div>
          <div>
            <div style={{ fontSize: 26, fontWeight: 700, color: edgeColor(rc.best_rule_edge_over_base_pct), fontFamily: "var(--font-mono)" }}>
              {signedPct(rc.best_rule_edge_over_base_pct)}
            </div>
            <div className="text-muted" style={{ fontSize: 12 }}>
              Best rule edge{winLabel && bin ? ` · ${winLabel}, ${bin[0]}-${bin[1]}% conv @ ${rc.best_rule!.horizon}d` : ""}
            </div>
          </div>
        </div>
      </CardBody>
    </Card>
  );
}

/**
 * OutOfSampleValidation — renders /api/oos-validation on the Track Record page.
 * Two honest counters to in-sample selection bias (whitepaper §6 / §7.2): a
 * per-window expanding walk-forward (the forecast horizon is re-committed on
 * prior folds, then scored out-of-sample) and a grid-wide White's Reality Check.
 */
export default function OutOfSampleValidation() {
  const data = useOosValidation();

  if (!data) return null;

  // `data.windows` is guaranteed to be an object by the hook (it merges the raw
  // payload over EMPTY), so this is safe even for an unpopulated OOS pkl.
  const windows = data.windows;
  // Record<> types windows[k] as always-defined, but a window with no scored fold
  // yet is genuinely absent, so this truthy filter is a real runtime guard.
  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
  const windowKeys = Object.keys(WINDOW_LABELS).filter(k => windows[k]);
  const rc = data.grid_reality_check;
  const hasContent = windowKeys.length > 0 || (rc && rc.n_rules > 0);

  if (!hasContent) {
    return (
      <Card className="mt-3">
        <CardBody className="text-center text-muted py-4">
          <p className="mb-1">Out-of-sample validation is still accruing.</p>
          <p className="mb-0" style={{ fontSize: 13 }}>
            The walk-forward needs several independent signal-dates per window before it can score a
            held-out fold. Check back as the track record matures.
          </p>
        </CardBody>
      </Card>
    );
  }

  return (
    <div className="mt-3">
      {rc && rc.n_rules > 0 && <RealityCheckPanel rc={rc} />}

      <Card>
        <CardHeader style={{ borderBottom: "1px solid var(--border-color)", background: "var(--card-background)" }}>
          <h5 className="mb-0" style={{ fontWeight: 700 }}>Walk-forward, out-of-sample</h5>
        </CardHeader>
        <CardBody>
          <p className="text-muted mb-3" style={{ fontSize: 13.5 }}>
            For each window we split the signals into chronological folds. Before scoring each fold we
            re-commit the forecast horizon to whichever performed best on the <em>prior</em> folds only —
            so the reported win rate was never chosen on the data it's measured against. Edge is versus the
            matched base rate (the blended bull/bear null), not a naïve 50%. CIs are date-clustered.
          </p>

          <Table responsive borderless size="sm" className="mb-1 tr-compact-table" style={{ fontSize: 13 }}>
            <thead>
              <tr style={{ color: "var(--text-muted)", borderBottom: "1px solid var(--border-color)" }}>
                <th>Window</th>
                <th className="text-end">OOS win rate</th>
                <th className="text-end">Edge over base</th>
                <th className="text-end">Avg return</th>
                <th className="text-end">Horizons / fold</th>
                <th className="text-end">Signals</th>
                <th className="text-end">Dates</th>
              </tr>
            </thead>
            <tbody>
              {windowKeys.map(k => {
                const w: OosWindow = windows[k];
                return (
                  <tr key={k}>
                    <td>{WINDOW_LABELS[k]}</td>
                    <td className="text-end">
                      {pct(w.oos_win_rate)}
                      {w.oos_win_rate_ci && (
                        <span className="text-muted" style={{ fontSize: 11 }}> ({ci(w.oos_win_rate_ci)})</span>
                      )}
                    </td>
                    <td className="text-end" style={{ color: edgeColor(w.oos_edge_over_base), fontWeight: 600 }}>
                      {signedPct(w.oos_edge_over_base)}
                    </td>
                    <td className="text-end">
                      {signedPct(w.oos_avg_return, 2)}
                    </td>
                    <td className="text-end text-muted" style={{ fontSize: 12 }}>
                      {w.committed_horizons_by_fold.length ? w.committed_horizons_by_fold.join(", ") : "—"}
                    </td>
                    <td className="text-end">{w.n_signals.toLocaleString()}</td>
                    <td className="text-end">{w.n_dates.toLocaleString()}</td>
                  </tr>
                );
              })}
            </tbody>
          </Table>

          <p className="text-muted mt-2 mb-0" style={{ fontSize: 11.5 }}>
            "Horizons / fold" shows the horizon re-committed before each held-out fold — stable numbers
            mean the best horizon is consistent through time. Edge over base uses each signal's matched
            null (base rate up for a bull call, 1 − base for a bear call). Signals issued on the same day
            are correlated, so "Dates" is the effective sample, not "Signals".
            {data.params && (
              <> Params: {data.params.n_folds} folds, min-train {data.params.min_train},
                {" "}{data.params.bootstrap_B.toLocaleString()} bootstrap resamples.</>
            )}
            {" "}Past performance does not guarantee future results.
          </p>
        </CardBody>
      </Card>
    </div>
  );
}
